/**
 * RaceStats Page
 *
 * Displays detailed stats for a past race including:
 * - Race metadata (date, question count)
 * - Podium (1st, 2nd, 3rd)
 * - Final leaderboard with placement, name, progress, accuracy
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchRaceStats, deleteRace, type RaceLiveStatsRow } from '@/hooks/useRaceHistory';
import { getAppById } from '@/config/apps';
import type { RaceRoom, RaceParticipant } from '@/types/race';
import styles from '@/components/Race/Race.module.css';

const APP_ICONS: Record<string, string> = {
  addition: '+',
  multiplication: '\u00d7',
  subtraction: '-',
  division: '\u00f7',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(startTime: string | null, endTime: string | null): string {
  if (!startTime || !endTime) return '-';
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const durationMs = end - start;
  const seconds = Math.floor(durationMs / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function formatTime(ms: number | null): string {
  if (!ms) return '-';
  const seconds = ms / 1000;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

interface LeaderboardEntry {
  rank: number;
  participant: RaceParticipant;
  correctCount: number;
  incorrectCount: number;
  cardsCompleted: number;
  accuracy: number;
  totalTime: number;
}

function buildLeaderboard(
  participants: RaceParticipant[],
  liveStats: RaceLiveStatsRow[]
): LeaderboardEntry[] {
  // Map live stats by user_id for lookup
  const statsByUser = new Map<string, RaceLiveStatsRow>();
  for (const row of liveStats) {
    statsByUser.set(row.user_id, row);
  }

  // Enrich participants with live stats
  const enriched = participants.map((participant) => {
    const lookupKey = participant.user_id || participant.id;
    const stats = statsByUser.get(lookupKey);

    return {
      participant,
      correctCount: stats?.correct_count ?? participant.correct_count ?? 0,
      incorrectCount: stats?.incorrect_count ?? participant.incorrect_count ?? 0,
      cardsCompleted: stats?.cards_completed ?? participant.total_cards ?? 0,
      totalTime: stats?.total_time ?? 0,
    };
  });

  // Sort by correct answers (desc), then by time (asc)
  const sorted = [...enriched].sort((a, b) => {
    if (a.correctCount !== b.correctCount) {
      return b.correctCount - a.correctCount;
    }
    const aTime = a.totalTime || Infinity;
    const bTime = b.totalTime || Infinity;
    return aTime - bTime;
  });

  return sorted.map((entry, index) => {
    const totalAnswers = entry.correctCount + entry.incorrectCount;
    const accuracy = totalAnswers > 0 ? (entry.correctCount / totalAnswers) * 100 : 0;

    return {
      rank: index + 1,
      participant: entry.participant,
      correctCount: entry.correctCount,
      incorrectCount: entry.incorrectCount,
      cardsCompleted: entry.cardsCompleted,
      accuracy,
      totalTime: entry.totalTime,
    };
  });
}

export function RaceStats() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [room, setRoom] = useState<RaceRoom | null>(null);
  const [participants, setParticipants] = useState<RaceParticipant[]>([]);
  const [liveStats, setLiveStats] = useState<RaceLiveStatsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    async function loadStats() {
      if (!id) {
        setError('No race ID provided');
        setLoading(false);
        return;
      }

      const result = await fetchRaceStats(id);
      if (result.error) {
        setError(result.error);
      } else {
        setRoom(result.room);
        setParticipants(result.participants);
        setLiveStats(result.liveStats);
      }
      setLoading(false);
    }

    loadStats();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this race? This action cannot be undone.'
    );

    if (!confirmed) return;

    setIsDeleting(true);
    const result = await deleteRace(id);

    if (result.success) {
      navigate('/race');
    } else {
      setError(result.error || 'Failed to delete race');
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.raceContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>Loading race stats...</span>
        </div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className={styles.raceContainer}>
        <div className={styles.errorState}>
          <h2>Error loading race</h2>
          <p>{error || 'Race not found'}</p>
          <button className={styles.secondaryButton} onClick={() => navigate('/race')}>
            Back to Race Dashboard
          </button>
        </div>
      </div>
    );
  }

  const leaderboard = buildLeaderboard(participants, liveStats);
  const podium = leaderboard.slice(0, 3);

  // Reorder for podium display: 2nd, 1st, 3rd
  const podiumDisplay = [
    podium[1], // 2nd place (left)
    podium[0], // 1st place (center)
    podium[2], // 3rd place (right)
  ].filter(Boolean);

  return (
    <div className={styles.raceContainer}>
      {/* Header with back button and delete */}
      <div className={styles.statsHeader}>
        <button className={styles.backButton} onClick={() => navigate('/race')}>
          &larr; Back
        </button>
        <div className={styles.statsHeaderInfo}>
          <h1 className={styles.pageTitle}>Race Results</h1>
          <p className={styles.statsDate}>{formatDate(room.created_at)}</p>
        </div>
        <button
          className={styles.deleteButton}
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>

      {/* Race Metadata */}
      <div className={styles.statsMeta}>
        <div className={styles.statsMetaItem}>
          <span className={styles.statsMetaValue}>{participants.length}</span>
          <span className={styles.statsMetaLabel}>Participants</span>
        </div>
        <div className={styles.statsMetaItem}>
          <span className={styles.statsMetaValue}>{room.question_count}</span>
          <span className={styles.statsMetaLabel}>Questions</span>
        </div>
        <div className={styles.statsMetaItem}>
          <span className={styles.statsMetaValue}>
            {formatDuration(room.race_start_time, room.finished_at)}
          </span>
          <span className={styles.statsMetaLabel}>Duration</span>
        </div>
      </div>

      {/* Podium */}
      {podium.length > 0 && (
        <div className={styles.podium}>
          {podiumDisplay.map((entry, displayIndex) => {
            if (!entry) return <div key={displayIndex} className={styles.podiumPlaceholder} />;

            const app = getAppById(entry.participant.app_name);
            const actualRank = entry.rank;
            const podiumHeight = actualRank === 1 ? 'tall' : actualRank === 2 ? 'medium' : 'short';

            return (
              <div
                key={entry.participant.id}
                className={`${styles.podiumPlace} ${styles[`podium${podiumHeight}`]}`}
              >
                <div className={styles.podiumAvatar}>
                  <span className={styles.podiumRank}>{actualRank}</span>
                </div>
                <div className={styles.podiumName}>{entry.participant.username}</div>
                <div
                  className={styles.podiumApp}
                  style={{ '--app-color': app?.color || '#666' } as React.CSSProperties}
                >
                  {APP_ICONS[entry.participant.app_name]} {app?.shortName}
                </div>
                <div className={styles.podiumStats}>
                  <span>{entry.correctCount} / {entry.cardsCompleted} correct</span>
                  <span>{Math.round(entry.accuracy)}% accuracy</span>
                </div>
                <div className={styles.podiumPedestal} />
              </div>
            );
          })}
        </div>
      )}

      {/* Full Results Table */}
      <div className={styles.resultsTable}>
        <h2 className={styles.resultsTableTitle}>All Participants</h2>

        <div className={styles.resultsTableHeader}>
          <span className={styles.colRank}>Rank</span>
          <span className={styles.colName}>Name</span>
          <span className={styles.colScore}>Score</span>
          <span className={styles.colAccuracy}>Accuracy</span>
          <span className={styles.colTime}>Time</span>
        </div>

        {leaderboard.map((entry) => (
          <div key={entry.participant.id} className={styles.resultsTableRow}>
            <span className={styles.colRank}>
              <span className={`${styles.rankBadge} ${entry.rank <= 3 ? styles[`rank${entry.rank}`] : ''}`}>
                {entry.rank}
              </span>
            </span>
            <span className={styles.colName}>{entry.participant.username}</span>
            <span className={styles.colScore}>
              {entry.correctCount} / {entry.cardsCompleted}
            </span>
            <span className={styles.colAccuracy}>{Math.round(entry.accuracy)}%</span>
            <span className={styles.colTime}>{formatTime(entry.totalTime)}</span>
          </div>
        ))}

        {leaderboard.length === 0 && (
          <div className={styles.resultsEmpty}>No participants in this race</div>
        )}
      </div>
    </div>
  );
}
